import {
  uploadPricelist,
  type PricelistUploadPhase,
  type UploadPricelistResult,
} from '@/api/inventory-management/uploadPricelist';
import type {
  BackgroundJobOutcome,
  BackgroundJobProgress,
  StartJobInput,
} from '@/context/background-jobs';
import type { TranslateFn } from 'src/lib/pricelist-utils';

/** Turn an upload phase into the tracker's status line and bar fraction. */
export function toPricelistUploadJobProgress(
  phase: PricelistUploadPhase,
  gt: TranslateFn,
): BackgroundJobProgress {
  if (phase.phase === 'uploading') {
    return {
      percent: phase.percent,
      label: gt('Uploading…', {
        context: 'background task status line while a pricelist file uploads',
      }),
    };
  }
  // No fraction: the server is running the file through the extraction model and
  // reports nothing until it answers, so the panel shows an indeterminate bar.
  return {
    // Format-neutral on purpose: the file being extracted is just as often a
    // PDF, spreadsheet, or image as a CSV.
    label: gt('Extracting tables…', {
      context:
        'background task status line while the server extracts a pricelist',
    }),
  };
}

/** The panel's "View" action: the file's own page. */
function viewAction(
  pdfDocumentId: string,
  gt: TranslateFn,
  viewDocument: (pdfDocumentId: string) => void,
): NonNullable<BackgroundJobOutcome['action']> {
  return {
    label: gt('View', {
      context: 'panel action opening the finished pricelist',
    }),
    run: () => viewDocument(pdfDocumentId),
  };
}

export function pricelistUploadJobOutcome(
  result: UploadPricelistResult,
  gt: TranslateFn,
  viewDocument: (pdfDocumentId: string) => void,
): BackgroundJobOutcome {
  // Not extracted at all (an Outlook .msg notice): stored and, on a
  // case, attached — there is nothing more to wait for.
  if (result.extraction === null) {
    return {
      label: gt('Saved.', {
        context:
          'background task status line after a non-extractable pricelist file (an email) is stored',
      }),
      action: viewAction(result.pdfDocumentId, gt, viewDocument),
    };
  }

  // Thrown rather than returned as a success with a caveat, so the panel
  // says "failed" and offers the copy-error action. The pricelist is still
  // there and its extraction can be re-run from its own page.
  if (result.extraction.outcome === 'failed') {
    throw new Error(
      result.extraction.errorMessage ??
        gt('Extraction failed. Re-run it from the pricelist.', {
          context:
            'background task error when pricelist extraction fails without a reason',
        }),
    );
  }
  if (result.extraction.outcome === 'vanished') {
    throw new Error(
      gt('Stopped tracking this extraction. Check the pricelist.', {
        context:
          'background task error when the extraction job can no longer be read',
      }),
    );
  }
  return {
    label: gt('Extracted and saved.', {
      context: 'background task status line after a pricelist is stored',
    }),
    action: viewAction(result.pdfDocumentId, gt, viewDocument),
  };
}

export function enqueuePricelistUploads({
  files,
  startJob,
  companyId,
  caseId,
  gt,
  viewDocument,
  invalidatePricelistViews,
  invalidateCaseViews,
}: {
  files: readonly File[];
  startJob: (input: StartJobInput) => string;
  companyId?: string;
  caseId?: string;
  gt: TranslateFn;
  viewDocument: (pdfDocumentId: string) => void;
  invalidatePricelistViews: () => void;
  invalidateCaseViews: () => void;
}): void {
  for (const file of files) {
    startJob({
      title: file.name,
      initialLabel: gt('Uploading…', {
        context: 'background task status line as a pricelist upload starts',
      }),
      // Not retryable from the panel: a failure after the server committed would
      // duplicate the pricelist on a blind re-run, so re-uploading stays a
      // deliberate act.
      run: async ({ setProgress }) => {
        const result = await uploadPricelist(
          {
            file,
            companyId,
            caseId,
            // The file is on the case the moment the claim commits; do not make
            // the case page wait out the extraction to show it.
            onStored: caseId === undefined ? undefined : invalidateCaseViews,
          },
          (phase) => setProgress(toPricelistUploadJobProgress(phase, gt)),
        );
        // Refreshed before the outcome is judged: the pricelist itself was stored
        // even when its extraction failed, so the lists should show it either way.
        // The case views go again too — the extraction changed the file's state.
        invalidatePricelistViews();
        if (caseId !== undefined) invalidateCaseViews();
        return pricelistUploadJobOutcome(result, gt, viewDocument);
      },
    });
  }
}
